import React, { useState, lazy, Suspense, ReactNode } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { SidebarNavigation } from '../navigation/SidebarNavigation';
import { HomePage } from '../pages/HomePage';
import { PageHeader } from './PageHeader';
import { EmptyState } from '../shared/EmptyState';
import { LoadingState } from '../shared/LoadingState';
import HITLValidationLayout from './HITLValidationLayout';
import { useAuth } from '../../contexts/AuthContext';
import { AttentionProvider } from '../../contexts/AttentionContext';
import { ROLES, UserRole } from '../../config/authConfig';
import { useWorkspaceDocument } from '../../hooks/useWorkspaceDocument';
import analyzerService, { AnalyzerConfiguration } from '../../services/analyzerService';

// Lazy load heavy components that aren't needed on initial page load
const UploadPage = lazy(() => import('../pages/UploadPage'));
const PlatformAdminPage = lazy(() => import('../pages/PlatformAdminPage'));
const TenantConfigPage = lazy(() => import('../pages/TenantConfigPage'));
const ReportsPage = lazy(() => import('../pages/ReportsPage'));
const LensSearchPage = lazy(() => import('../pages/LensSearchPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const ExceptionDashboardPage = lazy(() => import('../pages/ExceptionDashboardPage'));

// Loading fallback component
const PageLoader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
    <CircularProgress />
  </Box>
);

const AnalysisPage: React.FC = () => (
  <Box>
    <PageHeader
      title="Document Analysis"
      subtitle="Human-in-the-Loop document validation and analysis interface for reviewing AI-extracted data"
    />
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" color="text.secondary">
        Document analysis and validation interface will be available here.
      </Typography>
    </Box>
  </Box>
);

// Route guard: renders children only if the user holds `role` (or higher in the
// hierarchy). Reuses `hasRole` from AuthContext + `ROLES` from authConfig.
// Unauthorized users are redirected to the app root.
const RequireRole: React.FC<{ role: UserRole; children: ReactNode }> = ({ role, children }) => {
  const { hasRole } = useAuth();
  if (!hasRole(role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Shared failure surface for both document routes.
const DocumentLoadError: React.FC<{ error: string | null; onBack: () => void; backLabel: string }> = ({
  error,
  onBack,
  backLabel,
}) => (
  <Box sx={{ p: 3 }}>
    {/* `role="alert"` because this reports a failure — without it a screen-reader user gets no
        announcement that the document did not load, only silence where a document should be. */}
    <EmptyState
      role="alert"
      message="Unable to load document"
      description={error || 'Document not found'}
      minHeight="60vh"
      action={
        <Button variant="contained" onClick={onBack}>
          {backLabel}
        </Button>
      }
    />
  </Box>
);

// Review workspace (`/documents/:documentId`) — the editable HITL surface. Claims the review
// lock on open; see useWorkspaceDocument for why that is a per-route decision.
const DocumentDetailsLoader: React.FC = () => {
  const { documentId = '' } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const sourceGroup = (location.state as { sourceGroup?: 'stranded' | 'failed' | 'onHold' } | null)?.sourceGroup;
  const { extractedData, loading, error } = useWorkspaceDocument(documentId, { claimForReview: true });

  if (loading) {
    return <LoadingState message="Loading document…" minHeight="60vh" />;
  }

  if (error || !extractedData) {
    return <DocumentLoadError error={error} onBack={() => navigate('/')} backLabel="Back to Home" />;
  }

  return (
    <HITLValidationLayout
      extractedData={extractedData}
      documentId={documentId}
      onBack={() => navigate('/')}
      sourceGroup={sourceGroup}
      onNextDocument={(nextId: string) =>
        navigate(`/documents/${encodeURIComponent(nextId)}`, sourceGroup ? { state: { sourceGroup } } : undefined)
      }
    />
  );
};

// Lens read-only document view (`/lens/documents/:documentId`) — Lens S5.
//
// A SEPARATE ROUTE from the review workspace, deliberately. Read-only-ness is then a property of
// the URL rather than of a flag passed through render: there is no code path from a search result
// to the claim-and-release workspace to get wrong, and "opened from Lens" survives a bookmark,
// a refresh and a shared link. The two routes share one loader and one shell.
const LensDocumentRoute: React.FC = () => {
  const { documentId = '' } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { extractedData, record, loading, error } = useWorkspaceDocument(documentId, {
    claimForReview: false,
  });
  // Tri-state: `undefined` = not resolved (loading, or the load failed), `null` = resolved but this
  // tenant has no config for the type, object = resolved. The panel suppresses provenance badges
  // while it is `undefined`, so a reader never sees a trust signal appear or change after first paint.
  const [analyzerConfig, setAnalyzerConfig] = useState<AnalyzerConfiguration | null | undefined>(undefined);

  // Back returns to the search WITH the query intact. `LensSearchPage` keeps q/documentType/status/
  // from/to in URL params, so a bare navigate('/search') would drop the search a clerk just ran and
  // make them retype it once per document they open.
  const searchReturnTo = (location.state as { from?: string } | null)?.from ?? '/search';
  const backToSearch = () => navigate(searchReturnTo);

  // Field display names and per-field provenance come from the tenant's analyzer config, over the
  // same cached path the search screen uses. A failure is non-fatal for the document itself — the
  // record still renders — but it must NOT degrade into confident-looking labels, so it resolves
  // to "unknown" and the badges stay off.
  const documentType = record?.documentType;
  React.useEffect(() => {
    if (!documentType) return;
    let cancelled = false;
    setAnalyzerConfig(undefined);
    analyzerService
      .getAnalyzerConfigurations()
      .then((configs) => {
        if (cancelled) return;
        setAnalyzerConfig(configs.find((c) => c.documentType === documentType) ?? null);
      })
      .catch(() => {
        // Stays `undefined` — a failed config load must not read as "no provenance configured".
        if (!cancelled) setAnalyzerConfig(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [documentType]);

  if (loading) {
    return <LoadingState message="Loading document…" minHeight="60vh" />;
  }

  if (error || !extractedData) {
    return <DocumentLoadError error={error} onBack={backToSearch} backLabel="Back to search" />;
  }

  return (
    <HITLValidationLayout
      workspaceMode="lens"
      extractedData={extractedData}
      record={record}
      analyzerConfig={analyzerConfig}
      documentId={documentId}
      onBack={backToSearch}
    />
  );
};

// Home route — wires HomePage's document-click callback to the router.
const HomeRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <HomePage onDocumentClick={(documentId) => navigate(`/documents/${encodeURIComponent(documentId)}`)} />
  );
};

// Platform admin route — navigates to a tenant's config or exception detail on drill-in.
const PlatformAdminRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <PlatformAdminPage
      onNavigateToTenant={(tenantId, tenantName) =>
        navigate(`/admin/tenants/${encodeURIComponent(tenantId)}`, { state: { tenantName } })
      }
      onNavigateToTenantExceptions={(tenantId, tenantName) =>
        navigate(`/admin/tenants/${encodeURIComponent(tenantId)}/exceptions`, { state: { tenantName } })
      }
    />
  );
};

// PlatformAdmin drilling into a specific tenant's configuration.
const TenantConfigDrilldownRoute: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const tenantName = (location.state as { tenantName?: string } | null)?.tenantName;
  return (
    <TenantConfigPage
      tenantId={tenantId}
      tenantName={tenantName}
      onBack={() => navigate('/admin')}
      viewerRole="PlatformAdmin"
    />
  );
};

// PlatformAdmin drilling into a specific tenant's exception dashboard (UX8).
const TenantExceptionsDrilldownRoute: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const location = useLocation();
  const tenantName = (location.state as { tenantName?: string } | null)?.tenantName;
  return <ExceptionDashboardPage tenantId={tenantId} tenantName={tenantName} />;
};

// 404 — unknown route.
const NotFoundRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ p: 3 }}>
      <EmptyState
        icon={<SearchOffIcon sx={{ fontSize: 48 }} />}
        message="Page not found"
        description="The page you're looking for doesn't exist or may have moved."
        minHeight="60vh"
        action={
          <Button variant="contained" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        }
      />
    </Box>
  );
};

export const MainLayout: React.FC = () => {
  const { userRoles } = useAuth();
  const isPlatformAdmin = userRoles.includes(ROLES.PLATFORM_ADMIN);
  const isTenantAdmin = !isPlatformAdmin && userRoles.includes(ROLES.TENANT_ADMIN);

  return (
    <AttentionProvider>
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Sidebar Navigation */}
      <SidebarNavigation />

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Page Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            backgroundColor: 'background.default',
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* PlatformAdmin lands on /admin; TenantAdmin lands on the exception dashboard;
                  Analyst lands on the review queue (UX7 decision D5). */}
              <Route
                path="/"
                element={
                  isPlatformAdmin ? <Navigate to="/admin" replace />
                  : isTenantAdmin ? <Navigate to="/dashboard" replace />
                  : <HomeRoute />
                }
              />
              {/* Stable path to the review queue — TenantAdmin can still jump in to review
                  documents even though "/" now lands them on the dashboard. */}
              <Route path="/queue" element={<HomeRoute />} />
              <Route path="/documents/:documentId" element={<DocumentDetailsLoader />} />
              {/* Lens read-only archive view (S5) — reached from a search result. */}
              <Route path="/lens/documents/:documentId" element={<LensDocumentRoute />} />
              <Route path="/upload" element={<UploadPage />} />
              <Route path="/analysis" element={<AnalysisPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              {/* Search is scope-filtered server-side for every role, so it needs no RequireRole
                  guard — a user simply retrieves nothing outside their own models. */}
              <Route path="/search" element={<LensSearchPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route
                path="/dashboard"
                element={
                  <RequireRole role={ROLES.TENANT_ADMIN}>
                    <ExceptionDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireRole role={ROLES.PLATFORM_ADMIN}>
                    <PlatformAdminRoute />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/tenants/:tenantId"
                element={
                  <RequireRole role={ROLES.PLATFORM_ADMIN}>
                    <TenantConfigDrilldownRoute />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/tenants/:tenantId/exceptions"
                element={
                  <RequireRole role={ROLES.PLATFORM_ADMIN}>
                    <TenantExceptionsDrilldownRoute />
                  </RequireRole>
                }
              />
              <Route path="*" element={<NotFoundRoute />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </Box>
    </AttentionProvider>
  );
};
